import { Citation } from '@/store/modules/documents/types/citations.types';
import { PendingCitationData } from '@/jbi-shared/types/document.types';
import { get as _get } from 'lodash';

export const getCitationReferenceNumber = (
  citation: Citation | PendingCitationData,
  sortedReferenceCitationIds: string[] | number[]
): number | undefined => {
  const citationRefIndex = sortedReferenceCitationIds.findIndex(
    (value: string | number) => {
      return value.toString() === citation.id.toString();
    }
  );
  if (typeof citationRefIndex !== 'undefined' && citationRefIndex !== -1) {
    // Return reference number starting from 1 instead of array index starting from 0
    return citationRefIndex + 1;
  } else {
    return undefined;
  }
};

// Helper function to safely get string value
const safeGetString = (
  obj: unknown,
  path: string,
  defaultValue = ''
): string => {
  const value = _get(obj, path, defaultValue);
  return value != null ? String(value) : '';
};

// Common function to filter citations based on search text
// Safely handles non-string values that could cause toLowerCase() errors
export const filterCitationsBySearch = (
  citation: Citation | PendingCitationData,
  searchValue: string
): boolean => {
  if (!searchValue || !citation) return true;

  const searchLower = searchValue.toLowerCase();

  // Get author string - handle both array and string formats
  const getAuthorString = (
    citation: Citation | PendingCitationData
  ): string => {
    const authorData = _get(citation, 'content.author');
    if (Array.isArray(authorData)) {
      return transformAuthorArrayToString(authorData);
    }
    return safeGetString(citation, 'content.author');
  };

  if (citation.content?.plainText) {
    const citationText = safeGetString(
      citation,
      'content.plainText'
    ).toLowerCase();
    const author = getAuthorString(citation).toLowerCase();
    const title = safeGetString(citation, 'content.title').toLowerCase();

    return (
      author.includes(searchLower) ||
      title.includes(searchLower) ||
      citationText.includes(searchLower)
    );
  } else {
    const author = getAuthorString(citation).toLowerCase();
    const title = safeGetString(citation, 'content.title').toLowerCase();

    return author.includes(searchLower) || title.includes(searchLower);
  }
};

export const transformAuthorArrayToString = (
  authors:
    | Array<{ given?: string; family?: string; literal?: string }>
    | string
    | null
    | undefined
): string => {
  let authorString = '';

  if (Array.isArray(authors)) {
    // Handle author objects array
    for (const author of authors) {
      let authorValue = '';

      // Check if it's an organization author (has literal field)
      if (author.literal) {
        authorValue = author.literal;
      } else {
        // It's an individual author (has given/family fields)
        authorValue =
          (author.given ? author.given + ' ' : '') +
          (author.family ? author.family : '');
      }

      authorString =
        authorString !== ''
          ? authorString + ', ' + authorValue
          : authorString + authorValue;
    }
  } else if (typeof authors === 'string' && authors.trim()) {
    try {
      // Try to parse as JSON in case it's a stringified array
      const parsedAuthors = JSON.parse(authors);
      if (Array.isArray(parsedAuthors)) {
        return transformAuthorArrayToString(parsedAuthors);
      } else {
        // It's a regular string, use as is
        authorString = authors;
      }
    } catch (e) {
      // Not valid JSON, treat as regular string
      authorString = authors;
    }
  }

  return authorString;
};
